import PropTypes from 'prop-types';
import React, { useContext, useEffect } from 'react';

import { MainContext } from '@ezbobdev/ui-framework';
import { ScreenWrapper, styled } from '@ezbobdev/ui-widgets-ezbob';

import { wizardDirection } from '../../utils/constants';
import { useSteps } from '../../utils/useSteps';

const Wrapper = styled(ScreenWrapper)``;

const PageWrapper = (props) => {
  const {
    children,
    subtitleVariant,
    disclaimer,
    actions,
    customHeader,
    title,
    titleVariant,
    subtitle,
    backButton,
    maxWidth,
    fullWidth,
    contentFullWidth,
    className,
    isFinal,
    topImage,
    backgroundImage,
    variant,
    footer,
    showBackButtonArrowFallback,
    failurePointShown,
    ...rest
  } = props;

  const { actions: ctxActions } = useContext(MainContext);
  const { shouldMoveToStep } = useSteps(props);

  const moveBack = () => {
    if (typeof backButton.onMoveBackClick === 'function') {
      return backButton.onMoveBackClick();
    }
    return shouldMoveToStep(wizardDirection.backward);
  };
  const { pageName } = ctxActions.userState.getCurrentPageDetails() ?? {};
  const currentStepName = ctxActions.userState.getCurrentStepName();
  const actionsVariant = ctxActions.settings.getUserConfigProp(`custom.${currentStepName}.stepActionsVariant`);

  const screenWrapperBackgroundImage = ctxActions.settings.getUserConfigProp(
    `custom.${currentStepName || pageName}.screenWrapper.backgroundImage`,
  );

  const progressBarImage = ctxActions.settings.getUserConfigProp(
    `custom.${currentStepName || pageName}.screenWrapper.progressBarImage`,
  );

  const showBackButtonArrow =
    ctxActions.settings.getUserConfigProp(`custom.${currentStepName || pageName}.screenWrapper.showBackButtonArrow`) ??
    showBackButtonArrowFallback;

  const hasBackButton = backButton && backButton.show;

  // GRN-7081 / GRN-7198: expose the onboarding error code/name on the Datadog RUM view, mirroring the
  // GTM (useStep STEP_DID_MOUNT) and GA4 channels. `datadog.startView` is a bare passthrough to
  // datadogRum.startView, whose object form `{ name, context }` is the RUM SDK's own way of attaching
  // view context - there is no second argument. Tenants without an onboarding-event registry never
  // populate these case paths, so for them the call stays exactly `startView(currentStepName)`; keys
  // with undefined values are never added. GRN-7912 (D-07..D-10) adds `failurePointShown`, computed per
  // visit by InfoPageGL and passed as a prop only on the general `error` / `rejected` pages; when the prop
  // is a boolean the object form is used even without a code, otherwise the call is unchanged.
  const onboardingErrorCode = ctxActions.case?.getCaseValue('custom.onboardingErrorCode');
  const onboardingErrorName = ctxActions.case?.getCaseValue('custom.onboardingErrorName');

  useEffect(() => {
    if (ctxActions.externalServices.datadog) {
      const hasFlag = typeof failurePointShown === 'boolean';
      if (onboardingErrorCode || hasFlag) {
        ctxActions.externalServices.datadog.startView({
          name: currentStepName,
          context: {
            ...(onboardingErrorCode && { onboardingErrorCode }),
            ...(onboardingErrorCode && onboardingErrorName && { onboardingErrorName }),
            ...(hasFlag && { failurePointShown }),
          },
        });
      } else {
        ctxActions.externalServices.datadog.startView(currentStepName);
      }
    }
  }, [currentStepName, ctxActions, onboardingErrorCode, onboardingErrorName, failurePointShown]);

  return (
    <Wrapper
      className={className}
      title={title}
      titleVariant={titleVariant}
      subtitle={subtitle}
      subtitleVariant={subtitleVariant}
      customHeader={customHeader}
      onClickBack={hasBackButton ? moveBack : undefined}
      backButtonLabel={hasBackButton ? backButton.label : undefined}
      disclaimer={disclaimer}
      actions={actions}
      actionsVariant={actionsVariant}
      fullWidth={fullWidth}
      maxWidth={maxWidth}
      contentFullWidth={contentFullWidth}
      isFinalScreen={isFinal}
      topImage={topImage}
      backgroundImage={backgroundImage}
      variant={variant}
      screenWrapperBackgroundImage={screenWrapperBackgroundImage}
      progressBarImage={progressBarImage}
      footer={footer}
      showBackArrow={hasBackButton ? showBackButtonArrow : undefined}
      {...rest}
    >
      {children}
    </Wrapper>
  );
};

PageWrapper.propTypes = {
  className: PropTypes.string,
  children: PropTypes.node,
  actions: PropTypes.node,
  customHeader: PropTypes.node,
  title: PropTypes.string,
  subtitle: PropTypes.node,
  backButton: PropTypes.object,
  maxWidth: PropTypes.string,
  fullWidth: PropTypes.bool,
  isFinal: PropTypes.bool,
  contentFullWidth: PropTypes.bool,
  failurePointShown: PropTypes.bool,
};

export default PageWrapper;
